import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";
import { Link } from "react-router-dom";

function IslamicTradePage16() {
  const contentRef = useRef(null);
  const links = {
    prev: "/islamic-trade-page-15", 
    index: "/index",                 
    next: "/islamic-trade-page-17", 
  };
  const titleText = "Rulings on Freezing Assets (Hajr)";
  const questions = [
    {
      text: "1.  What is the primary purpose of implementing Hajr in Islamic finance??",
      options: [
        {
          value: "A",
          label: "A. To punish the debtor.",
        },
        {
          value: "B",
          label: "B. To confiscate wealth for the state.",
        },
        {
          value: "C",
          label: "C. To protect the rights of creditors and ensure justice.",
        },
        {
          value: "D",
          label: "D. To encourage investment by the debtor.",
        },
      ],
      correct: "C",
    },
    {
      text: "2. Which of the following individuals can have their assets frozen under Hajr?",
      options: [
        { value: "A", label: "A.  A person who has repaid all debts." },
        {
          value: "B",
          label: "B.  A debtor who has wealth but refuses to pay.",
        },
        { value: "C", label: "C. A creditor who demands repayment." },
        { value: "D", label: "D. A person with no financial dealings" },
      ],
      correct: "B",
    },
    {
      text: "3.  In the case of a mentally ill person, who is responsible for managing their wealth??",
      options: [
        { value: "A", label: "A. The person themselves." },
        { value: "B", label: "B. Any family member." },
        { value: "C", label: "C. Their father or legal guardian.y" },
        { value: "D", label: "D. The creditor." },
      ],
      correct: "C",
    },
    {
      text: "4.  What is the ruling on a financial contract executed by someone lacking financial intelligence?",
      options: [
        { value: "A", label: "A. Valid if witnessed by two people." },
        { value: "B", label: "B.  Valid only for small amounts." },
        { value: "C", label: "C. Conditionally valid." },
        { value: "D", label: "D.  Invalid." },
      ],
      correct: "D",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container" ref={contentRef}>
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={16}
        timerMinutes={60}
        currentLevel={1}
      />

      <p className="content">
        Welcome to <b>Day 16!</b> Today, we turn our attention to the important
        Islamic legal concept of Hajr, which refers to the freezing of assets in
        specific circumstances to uphold justice and protect financial rights.
        Whether it involves debtors who are unwilling or unable to repay, or
        individuals who are minors, mentally ill, or lack financial maturity,
        Hajr ensures that wealth is managed responsibly and ethically. By
        examining Quranic verses, Hadith, and scholarly applications, we will
        understand how this principle safeguards both creditors and the
        vulnerable, fostering a society grounded in fairness, trust, and
        compassion.
        <br /> In Islamic jurisprudence, Hajr refers to the legal provision of
        freezing a debtor’s assets when they are unable or unwilling to repay
        their debts. This measure safeguards the rights of the creditors while
        ensuring that justice and compassion are upheld in financial dealings.
        The concept is deeply rooted in Islamic teachings, balancing the
        protection of creditors with empathy toward debtors. <br />
        <br />
        Below, we explore the evidences for Hajr from the Quran and Sunnah, its
        wisdom, and the practical applications in three distinct scenarios.
        <br />
        <b>
          Understanding Hajr (Asset Freezing in Islam): Wisdom, Evidence, and
          Applications
        </b><br/>
        Evidence for Hajr in Quran and Sunnah: <br />
        <br /> <b>1. The Quran:</b> <br />
        Allah says: <br />
        "If it is difficult for someone to repay a debt, postpone it until a
        time of ease. And if you waive it as an act of charity, it will be
        better for you, if only you knew."
        <i>
          <a href="https://quran.com/2/280" target="blank">
            {" "}
            (Surah Al-Baqarah, 2:280)
          </a>
        </i>
        <br />
        This verse emphasizes leniency and compassion toward debtors who are
        genuinely unable to repay their debts. It establishes the foundation for
        treating debtors fairly while protecting creditors’ rights. <br />
        <br />
        <b>2. The Sunnah:</b>
        <br />
        The Prophet Muhammad (peace be upon him) said: <br />
        "Delay in payment by a rich man is injustice."{" "}
        <i>
          <a href="https://sunnah.com/bukhari:2400" target="blank">
            (Sahih al-Bukhari, Hadith 2400; Sahih Muslim, Hadith 1564)
          </a>
        </i>
        <br />
        This hadith underscores the need to differentiate between a debtor who
        cannot repay due to genuine hardship and one who deliberately delays
        payment despite having the means.
        <br />
        In another hadith, the Prophet (peace be upon him) stated: <br />
        "Whoever takes the wealth of people with the intention of repaying it,
        Allah will repay it on his behalf. And whoever takes it intending to
        destroy it, Allah will destroy him."{" "}
        <i>
          <a href="https://sunnah.com/bukhari:2387" target="blank">
            (Sahih al-Bukhari, Hadith 2387)
          </a>
        </i>
        <br />
        <br />
        <b>The Wisdom Behind Hajr</b>
        <br />
        <br />
        The wisdom behind Hajr is multifaceted, focusing on protecting financial
        justice and societal harmony: <br />
        <ul>
          <li>
            {" "}
            Protecting the Rights of Creditors: Ensures that creditors are not
            wronged and that their wealth is returned to them.
          </li>
          <li>
            Preventing Fraud and Misuse: Discourages debtors from unlawfully
            disposing of assets or evading repayment.
          </li>
          <li>
            {" "}
            Promoting Compassion and Social Support: Provides relief and
            fairness to debtors genuinely in hardship.
          </li>
          <li>
            Maintaining Transparency in Financial Transactions: Publicly
            announcing asset freezes ensures that others are aware and avoid
            fraudulent dealings with the debtor.{" "}
          </li>
          <li>
            {" "}
            Once the assets of the debtor have been freezed, he is not
            authorised to dispose any of those assets, albeit, he is permitted
            to incur a new debt to fulfill his needs.
          </li>
        </ul>
        <b>Three Situations of Hajr</b>
        <br />
        <br />
        1. The Debtor Is Completely Broke: In this situation, the debtor claims
        to have no wealth or assets to repay the loan. If the claim is verified
        to be genuine, it is impermissible to demand repayment immediately, as
        doing so would amount to oppression. <br />
        Instead, the lender must:{" "}
        <ul>
          <li>
            {" "}
            Grant an extension until the debtor is financially able to repay, or
          </li>
          <li> Forgive the loan as an act of charity, or</li>
          <li>
            Allow a Muslim ruler or community leader to volunteer to repay the
            loan on the debtor's behalf.{" "}
          </li>
        </ul>
        Example:
        <br />
        Ali lends Ahmad 5 BTC. Ahmad falls into financial hardship after losing
        his job and has no assets to his name. After verification, Ali grants
        Ahmad a year-long extension to repay or waives the debt entirely,
        earning immense rewards in the hereafter. <br />
        <br />
        2. The Debtor Has Wealth But Is Negligent: If the debtor has adequate
        wealth but is unwilling to repay despite having the means, Islamic law
        permits the ruler to intervene. <br />
        The debtor can be:
        <ul>
          <li> Forced to repay the debt through legal measures.</li>
          <li>
            Imprisoned and punished (within Islamic guidelines) to compel
            repayment.
          </li>
        </ul>
        Example:
        <br />
        Zayn owes Fatima 1 BTC. Zayn has the money but refuses to repay Fatima
        out of spite. Fatima files a case, and the ruler intervenes, compelling
        Zayn to settle the debt through the court's ruling. <br />
        <br />
        3. The Debtor Has Wealth Less Than the Debt Amount: When a debtor’s
        assets are insufficient to cover the loan, their assets are frozen. They
        are prohibited from selling, mortgaging, or gifting their assets until
        the debt is repaid. Scholars also recommend announcing the freeze
        publicly to ensure the lender’s heirs are aware of the debt in case of
        the lender’s passing and to prevent others from transacting with the
        debtor's frozen assets. <br />
        Example:
        <br />
        Sara owes Bilal 1.5 BTC but only has assets worth 0.8 BTC. Her assets
        are frozen to protect Bilal's rights, and she is prohibited from selling
        her car or property. This ensures that Sara’s creditors are eventually
        repaid from her assets once she regains financial stability. <br />
        <br />
        <b>Public Announcement of Hajr</b> <br />
        <br />
        Scholars have emphasized the importance of public announcements for Hajr
        in cases where assets are frozen. This ensures societal transparency and
        protects innocent third parties from unknowingly transacting with the
        debtor's frozen assets. It also helps heirs of creditors claim their due
        rights in the absence of the creditor. <br />
        The provision of Hajr in Islamic jurisprudence exemplifies the balance
        between justice and compassion. It safeguards creditors' rights while
        protecting debtors from undue hardship. By addressing each situation,
        whether the debtor is completely broke, negligent, or partially capable
        of repayment, Hajr ensures fairness and transparency in financial
        dealings, fostering a society grounded in trust, empathy, and justice.
        <br />
        <br />
        <b>
          Protecting the Vulnerable: Asset Freezing in Islam for Minors, the
          Mentally Ill, and the Financially Incapable
        </b>
        <br />
        <br />
        In Islamic law, the concept of Hajr (asset freezing) extends beyond
        financial disputes and debtor-creditor relationships. It also
        encompasses scenarios where an individual's assets are protected due to
        personal circumstances, such as mental illness, immaturity, or a lack of
        financial acumen. This provision ensures that individuals who are unable
        to manage their wealth responsibly are safeguarded from exploitation,
        and their resources are preserved for their benefit and the welfare of
        their dependents. Rooted in compassion and wisdom, this aspect of Hajr
        emphasizes the Islamic principle of looking after the vulnerable members
        of society while upholding justice. This lesson explores the application
        of Hajr in such contexts, drawing from Islamic teachings and offering
        practical insights into how wealth management is guided by the
        principles of care and responsibility.
        <br /> However, a small amount of money can be given to the minor and
        the one who lacks financial intelligence in accordance with what is
        customary, as for the mentally ill, then he is not to be given any thing
        from the assets he owns, rather a custodian will take care of his entire
        wealth on his behalf. <br /> The previously mentioned people from the
        three categories are not to be given any debt as well, nor are they
        elligible to execute any financial contracts. <br /> If they execute a
        contract then it is invalid.
        <br />
        The Guardian of the wealth of these people will be their father, or the
        one who has been chosen as the guardian in a will in case of
        inheritance, if none of these are available, then the Muslim ruler will
        be the guardian of their wealth.
        <br />
        <br />
        <b>Summary of Today's Lesson</b>
        <br />
        <br />
        Today’s lesson focused on the Islamic ruling of Hajr, which refers to
        the freezing of assets in specific situations to uphold justice and
        protect financial interests. We learned that Hajr applies in three key
        scenarios: when a debtor is completely broke, when they have wealth but
        refuse to repay, and when their assets are insufficient to cover the
        debt. Additionally, Hajr is used to protect the wealth of vulnerable
        individuals such as minors, the mentally ill, and those lacking
        financial acumen. The lesson emphasized that such measures are not
        punitive but are rooted in compassion, transparency, and the
        preservation of rights within society.
        <br />
        <br />
        <b>Coming up next</b>
        <br />
        <br />
        On Day 17 (tomorrow), we will explore the concept of Wikalah (Proxy) in
        Islamic financial transactions. Wikalah refers to appointing someone to
        act on your behalf in matters of trade, contracts, or representation. We
        will discuss its legal basis from the Quran and Sunnah, its conditions,
        types, and applications in everyday life and modern financial systems.
        Through this, you will understand how Islam facilitates ease and
        delegation in financial dealings while ensuring accountability and
        ethical conduct.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

        <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default IslamicTradePage16;
